import React, { useEffect, useState, useRef } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { trackifyPageChange } from "@allianz/tracking";

//* NDBX Components
import {
	Button,
	ButtonStack,
	FormProvider,
	IconButton,
	Section,
	NotificationCard,
} from "@arp/react-ndbx";

//* MUI Components
import useTheme from "@mui/material/styles/useTheme";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Fade, Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { useMutation, useQuery } from "@tanstack/react-query";

//* Life Components
import Page from "../../../components/Page";
import AuraComponentFactory from "../../../factory/AuraComponentFactory";
import PageLoadingBackdrop from "../../../components/organisms/PageLoadingBackdrop";
import SaveScriptModal from "../../../components/pageLevel/saveScriptModal/SaveScriptModal";

//* Hooks
import { useGetQuote } from "../../../hooks/dataHooks/useGetQuoteData";
import {
	answerAuraQuestion,
	getAllQuestions,
} from "../../../hooks/useAuraService";
import usePreQualification from "../../../hooks/usePreQualification.ts";
import usePreQualificationPages from "../../../hooks/usePreQualificationPages.ts";
import useSharedData from "../../../hooks/useSharedData";

//* Config and Untils
import {
	generateSchema,
	isDropDownNumericQuestion,
} from "../../../schema/auraQuestionsSchema";
import {
	getPatchAnswerPayload,
	getPreviousAnsweredValue,
	getPreviousAuraQuestion,
	getTheLatestQuestion,
} from "../../../utilities/auraHealthUtils";
import { AURA_QUESTION, AURA_STATEMENT } from "../../../config/const/enums";

//-----------------------------------------------------------------------
function AboutYouQuestions() {
	const { state, funcs } = usePreQualificationPages();
	const { currentPage } = { ...state };
	const { dataSet } = usePreQualification();
	const { personOne } = { ...dataSet };
	const [nextAuraQuestion, setNextAuraQuestion] = useState();
	const [unitizedDropdownOption, setUnitizedDropdownOption] = useState();
	const [auraErrorNotification, setAuraErrorNotification] = useState(false);
	const [ansRemapDropDwnNumericCount, setAnsRemapDropDwnNumericCount] =
		useState(0);
	const { data: quoteData, isLoading: quoteIsLoading } = useGetQuote();
	const { accessToken, handleEventChange } = useSharedData();
	const theme = useTheme();

	const nextStepRef = useRef(null);

	const circularProgressStyle = {
		color: `${theme.palette.action.PrimaryAction450}`,
	};

	const { isLoading, data, error, isRefetching, refetch } = useQuery({
		queryKey: ["auraAllQuestion"],
		queryFn: () => getAllQuestions(accessToken),
		refetchOnWindowFocus: false,
	});

	const mutation = useMutation((payload) =>
		answerAuraQuestion(payload, accessToken)
	);

	useEffect(() => {
		if (data?.responseJson?.length > 0) {
			let newReturnAuraQuestion = getTheLatestQuestion(data);
			setNextAuraQuestion(newReturnAuraQuestion);
		}
	}, [isLoading]);

	useEffect(() => {
		if (nextAuraQuestion?.answer) {
			if (nextAuraQuestion.type === AURA_QUESTION.AURA_BLOOD_PRESSURE) {
				const bloodPressurePrev = getPreviousAnsweredValue(nextAuraQuestion);
				setValue(
					AURA_QUESTION.AURA_BP_SYSTOLIC_READING,
					bloodPressurePrev.systolicReading,
					{ shouldValidate: true }
				);
				setValue(
					AURA_QUESTION.AURA_BP_DIASTOLIC_READING,
					bloodPressurePrev.diastolicReading,
					{ shouldValidate: true }
				);
			} else if (
				nextAuraQuestion.type === AURA_QUESTION.AURA_UNITIZED &&
				isDropDownNumericQuestion(nextAuraQuestion)
			) {
				const dropdownNumericPrev = getPreviousAnsweredValue(nextAuraQuestion);
				setValue(
					AURA_QUESTION.AURA_UNITIZED_VALUE,
					dropdownNumericPrev.unitizedValue,
					{ shouldValidate: true }
				);
				setValue(
					AURA_QUESTION.AURA_UNITIZED_OPTION,
					dropdownNumericPrev.unitizedOption,
					{ shouldValidate: true }
				);
			} else {
				setValue(
					AURA_QUESTION.AURA_QUESTION_NAME,
					getPreviousAnsweredValue(nextAuraQuestion),
					{ shouldValidate: true }
				);
			}
		} else {
			reset({ healthQuestion: "" });
		}
		if (nextAuraQuestion?.id) {
			trackifyPageChange(
				currentPage?.identifier?.toLowerCase() + "-id-" + nextAuraQuestion?.id
			);
		}
	}, [nextAuraQuestion]);

	useEffect(() => {
		if (unitizedDropdownOption && ansRemapDropDwnNumericCount > 0) {
			resetField(AURA_QUESTION.AURA_UNITIZED_VALUE);
			setValue(AURA_QUESTION.AURA_UNITIZED_VALUE, "");
		}
	}, [unitizedDropdownOption, ansRemapDropDwnNumericCount]);

	const schema = generateSchema(nextAuraQuestion, unitizedDropdownOption);
	const methods = useForm({
		resolver: yupResolver(schema),
		// defaultValues: {},
		mode: "all",
		reValidateMode: "all",
		shouldUnregister: true,
	});

	const {
		handleSubmit,
		control,
		formState: { errors, isValid },
		reset,
		resetField,
		setValue,
		getValues,
	} = methods;

	const handleValidationAndSubmit = async () => {
		try {
			methods.trigger().then(async (isValid) => {
				if (isValid) {
					const formData = getValues();
					let payload = getPatchAnswerPayload(nextAuraQuestion, formData);
					payload.id = nextAuraQuestion.id;
					payload.etag = nextAuraQuestion.etag;
					handleEventChange({ name: "isPageLoading", value: true });
					const answerResponse = await mutation.mutateAsync(payload);
					if (answerResponse?.status === 200) {
						await validateFetchNextQuestion(answerResponse);
					} else {
						await auraConflictAndErrorHandling(answerResponse, formData);
					}
					handleEventChange({ name: "isPageLoading", value: false });
				}
			});
		} catch (error) {
			console.error(
				"Error while submitting the form and calling the answer API - ",
				error
			);
		}
	};

	const validateFetchNextQuestion = async (answerResponse) => {
		const answerResponseObj = answerResponse?.data?.responseJson;
		const isUnderWritingComplete =
			answerResponseObj["_links"]?.sessionStatus.completed;
		if (isUnderWritingComplete) {
			// call the validate and proceed and check if it goes to the next page
			await funcs.validateAndProceed();
		} else {
			const returnAuraQuestionList = await refetch();
			const replyReturnAuraQuestion = getTheLatestQuestion(
				returnAuraQuestionList.data,
				nextAuraQuestion.id
			);
			setAuraErrorNotification(false);
			setNextAuraQuestion(replyReturnAuraQuestion);
			setAnsRemapDropDwnNumericCount(0);
		}
	};

	const auraConflictAndErrorHandling = async (answerResponse, formData) => {
		if (answerResponse?.response?.status === 409) {
			const latestAuraQuestionList = await refetch();
			let payloadReanswer = getPatchAnswerPayload(nextAuraQuestion, formData);
			payloadReanswer.id = nextAuraQuestion?.id;
			payloadReanswer.etag = latestAuraQuestionList?.data?.etag;
			const conflictAnswerResponse =
				await mutation.mutateAsync(payloadReanswer);
			if (conflictAnswerResponse?.status === 200) {
				await validateFetchNextQuestion(conflictAnswerResponse);
			} else {
				setAuraErrorNotification(true);
			}
		} else {
			setAuraErrorNotification(true);
		}
	};

	const getPreviousQuestion = async () => {
		handleEventChange({ name: "isPageLoading", value: true });
		const returnAuraQuestionList = await refetch();
		let previousAuraQuestion = getPreviousAuraQuestion(
			returnAuraQuestionList.data,
			nextAuraQuestion.id
		);
		handleEventChange({ name: "isPageLoading", value: false });
		setAuraErrorNotification(false);
		setNextAuraQuestion(previousAuraQuestion);
	};

	//** Modal Script */
	const [openSaveModal, setOpenSaveModal] = useState(false);
	const handleSaveModalOpen = () => {
		setOpenSaveModal(true);
	};
	const handleSaveModalClose = (event, reason) => {
		if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
			setOpenSaveModal(false);
		}
	};
	return (
		<Page title={currentPage.title} identifier={currentPage.identifier}>
			<PageLoadingBackdrop />
			<Section mb={{ xs: 4 }} mt={2}>
				<Typography variant="h2-bold">About you</Typography>
			</Section>
			<Section md={7} mb={1}>
				<Typography variant="label-large-regular">
					Hi {personOne.firstName}, this is your application progress
				</Typography>
			</Section>
			<Section md={7} mb={4}>
				<Typography variant="label-large-bold">
					Quote reference: {quoteData?.quoteInformation?.quoteNumber}
				</Typography>
			</Section>
			{auraErrorNotification && (
				<Section
					md={7}
					mb={4}
					sx={{
						pl: { xs: 1, sm: 8 },
						pr: { xs: 1, sm: 8 },
					}}
				>
					<Fade in={true} timeout={500}>
						<Box>
							<NotificationCard
								cardType="error"
								title=""
								message={AURA_STATEMENT.AURA_ERROR_MESSAGE}
							/>
						</Box>
					</Fade>
				</Section>
			)}
			{nextAuraQuestion ? (
				<>
					<FormProvider methods={methods}>
						<Section
							sx={{ px: 3, pb: 3 }}
							mb={0}
							mt={0}
							key={nextAuraQuestion.id}
						>
							<AuraComponentFactory
								componentType={nextAuraQuestion.type}
								nextAuraQuestion={nextAuraQuestion}
								control={control}
								defaultValue={getPreviousAnsweredValue(nextAuraQuestion)}
								setUnitizedDropdownOption={setUnitizedDropdownOption}
								setAnsRemapDropDwnNumericCount={setAnsRemapDropDwnNumericCount}
							/>
						</Section>
						<Section mt={6}>
							<ButtonStack>
								<Button
									variant="contained"
									color="secondary"
									disabled={isRefetching || mutation.isLoading}
									onClick={handleSaveModalOpen}
								>
									SAVE PROGRESS
								</Button>
								<Button
									variant="contained"
									color="primary"
									inputRef={nextStepRef}
									onClick={handleValidationAndSubmit}
									disabled={
										!isValid || isLoading || isRefetching || mutation.isLoading
									}
								>
									Next
								</Button>
							</ButtonStack>
							{nextAuraQuestion && nextAuraQuestion.id != 1 && (
								<Section md={8} mt={3} ml={-4}>
									<IconButton
										name="auraQuestionBack"
										icon={<ArrowBackIcon />}
										position="start"
										label="Back"
										variant="icon-button"
										onClick={getPreviousQuestion}
										disabled={isRefetching || mutation.isLoading}
									>
										Back
									</IconButton>
								</Section>
							)}
						</Section>
						<Section>
							<SaveScriptModal
								openSaveScriptModal={openSaveModal}
								handleSaveScriptModal={handleSaveModalClose}
								quoteNumber={quoteData?.quoteInformation?.quoteNumber}
							/>
						</Section>
					</FormProvider>
				</>
			) : (
				<CircularProgress style={circularProgressStyle} />
			)}
		</Page>
	);
}

export default AboutYouQuestions;
